import { isMemberEventsPageEnabled } from '@/lib/portal/constants';
import { isDocumentTelemetryEligible, isDocumentTelemetryPending } from '@/lib/privacy/eligibility';

/** Full-document login. Not on the analytics allowlist, so the boot guard denies it. */
export const TELEMETRY_DENIED_LOGIN_PATH = '/login';

export type AuthTab = 'sign-in' | 'sign-up';

/**
 * True when this document already loaded analytics, or the boot guard allowed it to.
 * Sign-in must not send an OTP or call setSession here.
 */
export function documentLoadedAnalytics(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    if (isDocumentTelemetryPending() || isDocumentTelemetryEligible()) return true;
    return Boolean(
      document.querySelector('script[src*="googletagmanager.com"], script[src*="google-analytics.com"]'),
    );
  } catch {
    return true;
  }
}

const RETURN_BASE = 'https://return.invalid';
const SENSITIVE_KEYS = new Set(['token', 'sharetoken', 'tokenhash', 'accesstoken', 'refreshtoken']);

function currentPath(): string {
  try {
    return window.location.pathname || '/';
  } catch {
    return '/';
  }
}

function fullyDecode(value: string): string {
  let out = value;
  for (let i = 0; i < 3; i += 1) {
    let next: string;
    try {
      next = decodeURIComponent(out);
    } catch {
      break;
    }
    if (next === out) break;
    out = next;
  }
  return out;
}

function hasControlChar(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code <= 31 || code === 127) return true;
  }
  return false;
}

function stripControlsAndSpace(value: string): string {
  let out = '';
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code <= 32) continue;
    out += value[i];
  }
  return out;
}

function normalizedKey(key: string): string {
  return fullyDecode(key).toLowerCase().replace(/[_-]/g, '');
}

function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEYS.has(normalizedKey(key));
}

/** Path (not query values) that would navigate off this origin or run a script. */
function dangerousPath(value: string): boolean {
  const decoded = stripControlsAndSpace(fullyDecode(value));
  const lower = decoded.toLowerCase();
  const pathOnly = lower.split('?')[0]?.split('#')[0] ?? lower;
  if (pathOnly.includes('\\')) return true;
  if (pathOnly.startsWith('//')) return true;
  if (pathOnly.includes('://')) return true;
  if (pathOnly.includes('javascript:')) return true;
  if (lower.startsWith('javascript:') || lower.startsWith('//')) return true;
  return false;
}

function sameOriginUrl(raw: string): URL | null {
  try {
    const url = new URL(raw, RETURN_BASE);
    if (url.origin !== RETURN_BASE) return null;
    if (url.username || url.password) return null;
    if (!url.pathname.startsWith('/') || url.pathname.startsWith('//')) return null;
    if (url.href.includes('\\')) return null;
    return url;
  } catch {
    return null;
  }
}

function stripSensitiveQuery(search: string): string {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  if (!raw) return '';
  const kept = raw.split('&').filter((part) => {
    if (!part) return false;
    const key = part.split('=')[0] ?? '';
    return !isSensitiveKey(key);
  });
  return kept.length > 0 ? `?${kept.join('&')}` : '';
}

function safeHash(hash: string): string {
  if (!hash || hash === '#') return '';
  const body = hash.slice(1);
  const decoded = fullyDecode(body);
  const compact = stripControlsAndSpace(decoded).toLowerCase();
  if (compact.includes('\\') || compact.includes('//') || compact.includes('javascript:') || compact.includes('://')) {
    return '';
  }
  try {
    const params = new URLSearchParams(body);
    for (const key of params.keys()) {
      if (isSensitiveKey(key)) return '';
    }
  } catch {
    return '';
  }
  return hash;
}

/**
 * Same-origin relative return path. Drops token query params and an unsafe hash.
 * Absolute URLs, protocol-relative hosts, backslashes, and javascript: (including
 * encoded forms) fall back to `/`.
 */
export function sanitizeAuthReturnPath(input: string | null | undefined): string {
  if (typeof input !== 'string' || input.length === 0 || input.length > 2048) return '/';
  if (hasControlChar(input)) return '/';
  if (!input.startsWith('/') || input.startsWith('//')) return '/';
  if (dangerousPath(input)) return '/';

  const decoded = fullyDecode(input);
  if (decoded !== input) {
    if (hasControlChar(decoded)) return '/';
    if (!decoded.startsWith('/') || decoded.startsWith('//') || dangerousPath(decoded)) return '/';
  }

  const url = sameOriginUrl(input);
  if (!url) return '/';
  if (decoded !== input && !sameOriginUrl(decoded)) return '/';

  const candidate = `${url.pathname}${stripSensitiveQuery(url.search)}${safeHash(url.hash)}`;
  if (!candidate.startsWith('/') || candidate.startsWith('//') || dangerousPath(candidate)) return '/';
  if (!sameOriginUrl(candidate)) return '/';
  return candidate;
}

function safeReturnPath(): string {
  try {
    const path = `${window.location.pathname || '/'}${window.location.search || ''}${window.location.hash || ''}`;
    return sanitizeAuthReturnPath(path);
  } catch {
    return '/';
  }
}

/**
 * Leave an analytics document with a full navigation before any OTP or setSession.
 * Returns true when the caller must stop. On a document without analytics, returns false
 * so the in-page modal can continue.
 */
export function leaveForTelemetryDeniedLogin(
  tab: AuthTab,
  assign: (url: string) => void = (url) => window.location.assign(url),
): boolean {
  if (!documentLoadedAnalytics()) return false;
  const path = currentPath();
  if (path === TELEMETRY_DENIED_LOGIN_PATH || path.startsWith(`${TELEMETRY_DENIED_LOGIN_PATH}/`)) {
    return true;
  }
  try {
    window.sessionStorage.setItem('authRedirectPath', safeReturnPath());
  } catch {
    /* still leave the analytics document */
  }
  const next = new URL(TELEMETRY_DENIED_LOGIN_PATH, window.location.origin);
  next.searchParams.set('tab', tab === 'sign-up' ? 'sign-up' : 'sign-in');
  assign(next.href);
  return true;
}

/** Where members land after sign-in when it started from the home page (REN-513). */
export const MEMBER_HOME_PATH = '/portal';

/**
 * A sign-in that started on `/` (or with no stored page) lands on the members'
 * "your events" page when that page is switched on. Any other page is kept.
 */
export function signedInLanding(path: string, eventsPageOn: boolean = isMemberEventsPageEnabled()): string {
  if (!eventsPageOn) return path;
  const pathname = path.split(/[?#]/)[0];
  return pathname === '/' ? MEMBER_HOME_PATH : path;
}

export function readAuthReturnPath(): string {
  let stored = '/';
  try {
    stored = window.sessionStorage.getItem('authRedirectPath') || '/';
    window.sessionStorage.removeItem('authRedirectPath');
  } catch {
    stored = '/';
  }
  return signedInLanding(sanitizeAuthReturnPath(stored));
}

/** Full navigation back to the page that started sign-in. Never leaves this origin. */
export function assignAuthReturn(
  assign: (url: string) => void = (url) => window.location.assign(url),
): void {
  const path = readAuthReturnPath();
  let target: URL;
  try {
    target = new URL(path, window.location.origin);
  } catch {
    assign(new URL('/', window.location.origin).href);
    return;
  }
  if (target.origin !== window.location.origin) {
    assign(new URL('/', window.location.origin).href);
    return;
  }
  assign(target.href);
}
